<template>
    <Dialog v-if="visible" size="small">
        <template v-slot:title>
            無線機設定の作成
        </template>

        <template v-slot:body>
            <div style="margin: 20px;">
                <el-form
                    label-position="left"
                    label-width="170px"
                    :model="form"
                    :rules="rules"
                    ref="form"
                >
                    <FormTextBox
                        v-model="form.radioSettingsName"
                        @input="handleRadioSettingsNameInput"
                        label="無線機設定名"
                        prop="radioSettingsName"
                        :required="true"
                        :disable="false"
                        placeholder="入力してください"
                    />

                    <el-form-item label="既存の設定を複製する">
                        <el-switch
                            v-model="form.copy"
                            active-color="#5171cb"
                            @change="onChangeCopy"
                        />
                    </el-form-item>

                    <el-form-item label="複製する設定" prop="copySettingId">
                        <el-select
                            v-model="form.copySettingId"
                            placeholder="選択してください"
                            :disabled="!form.copy"
                        >
                            <el-option v-for="setting in existingSettings" :key="setting.id" :label="setting.displayName" :value="setting.id"/>
                        </el-select>
                    </el-form-item>
                </el-form>
            </div>
        </template>

        <template v-slot:button>
            <BaseButton type="default" label="キャンセル" :disable="false" @click="handleCancelClick" />
            <BaseButton type="primary" label="保存する" :disable="disableSave" @click="handleSaveClick" />
        </template>
    </Dialog>
</template>

<script>
import Dialog from '@/components/pc/common/Dialog.vue'
import BaseButton from '@/components/pc/radio/ipradio/atoms/BaseButton.vue'
import FormTextBox from '@/components/pc/radio/ipradio/atoms/FormTextBox.vue'

export default {

    components: {
        Dialog,
        BaseButton,
        FormTextBox
    },

    props: {
        visible: {
            type: Boolean,
            required: true,
        },

        modelName: {
            type: String,
            required: true
        },

        existingSettings: {
            type: Array,
            required: false,
            default: () => []
        }
    },

    data() {
        return {
            form: {
                radioSettingsName: '',
                copy: false,
                copySettingId: null,
            },
            formValid: true,
            rules: {
                radioSettingsName: [
                    {
                        validator: (rule, value, callback) => {
                            // 空白文字のみチェック（全角・半角スペース）
                            if (/^[\s　]+$/.test(value)) {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            // 空文字チェック
                            if (!value || value.trim() === '') {
                                callback(new Error('値を指定してください。'))
                                return
                            }

                            // 文字数制限チェック (50文字以下)
                            if (value.length > 50) {
                                callback(new Error('50文字以下で指定してください。'))
                                return
                            }

                            // 「設定なし」チェック
                            if (value.trim() === '設定なし') {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            // バックスラッシュチェック
                            if (value.includes('\\')) {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            // 絵文字チェック（Unicode絵文字の範囲をチェック）
                            const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu;
                            if (emojiRegex.test(value)) {
                                callback(new Error('\\ や絵文字、空文字および「設定なし」は使用できません。'))
                                return
                            }

                            this.form.radioSettingsName = value.trim()
                            callback()
                        },
                        trigger: ['blur', 'change']
                    }

                ],
                copySettingId: [
                    {
                        validator: (rule, value, callback) => {
                            // 「複製する」をONにした場合のみ必須
                            if (this.form && this.form.copy) {
                                if (!value) {
                                    callback(new Error('複製する設定を選択してください。'))
                                    return
                                }
                            }
                            callback()
                        },
                        trigger: 'change'
                    }
                ]
            }
        }
    },

    computed: {
        disableSave() {
            const hasValidName = this.form.radioSettingsName && this.form.radioSettingsName.trim().length > 0;

            if (this.form.copy) {
                return !(hasValidName && this.form.copySettingId && this.formValid);
            }

            return !(hasValidName && this.formValid);
        }
    },

    watch: {
        visible(newVal) {
            if (newVal) {
                this.initialize()
            }
        }
    },

    created() {
        this.initialize()
    },

    methods: {
        closeDialog() {
            this.$emit('update:visible', false)
        },

        initialize() {
            // フォーム初期化
            const now = new Date();
            const pad = (n) => n.toString().padStart(2, '0');
            const dateOnly = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
            this.form = {
                radioSettingsName: this.modelName + '_' + dateOnly,
                copy: false,
                copySettingId: null,
            }
            this.formValid = true

            // 既にフォームがマウントされている場合はリセット
            if (this.$refs && this.$refs.form && typeof this.$refs.form.resetFields === 'function') {
                this.$nextTick(() => this.$refs.form.resetFields())
            }
        },

        handleRadioSettingsNameInput(value) {
            const sanitizedValue = typeof value === 'string' ? value.replace(/[\s　]/g, '') : ''
            if (sanitizedValue !== this.form.radioSettingsName) {
                this.form.radioSettingsName = sanitizedValue
            }

            if (!this.$refs.form) {
                return
            }

            this.$refs.form.validateField('radioSettingsName', (message) => {
                this.formValid = !message
            })
        },

        onChangeCopy(val) {
            if (!val) {
                this.form.copySettingId = null
                // OFFにしたら当該フィールドのバリデーションエラーを消す
                if (this.$refs && this.$refs.form) {
                    this.$refs.form.clearValidate && this.$refs.form.clearValidate('copySettingId')
                }
            }
        },

        handleSaveClick() {
            const formRef = this.$refs.form
            formRef.validate((valid) => {
                this.formValid = valid
                if (!valid) {
                    return
                }

                this.$emit('save', this.form.radioSettingsName, this.form.copySettingId)
            })
        },

        handleCancelClick() {
            this.closeDialog()
        }
    }
}
</script>

<style scoped>
</style>
